import { type RegisterOptions } from 'react-hook-form';

import { PHONE_CODES } from '../countries/constants';

import { validateDay, validateDobRange } from './helpers';
import { type UserSignupDetails } from './types';

export const EMAIL_SIGN_UP_FORM_SESSION_STORAGE_KEY =
  'email_signup_form_details';

export const SSO_SIGN_UP_CREDENTIALS_SESSION_STORAGE_KEY =
  'sso_signup_credentials';

export const SIGNUP_ERROR_SESSION_STORAGE_KEY = 'signup_error_details';

export const SIGNUP_SUCCESS_QUERY_PARAM = 'signupSuccess';

export const EMAIL_SIGN_UP_DEFAULT_FORM_VALUES: UserSignupDetails = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  marketingConsent: false,
  country: '',
};

export const SOCIAL_SIGN_UP_DEFAULT_FORM_VALUES: UserSignupDetails = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  marketingConsent: false,
  country: '',
};

export const blacklistedSignupPaths = [
  /^\/login\//,
  /^\/signup\//,
  /^\/password-reset\//,
];
export const PROD_HOST = 'https://depop.com';

export const SIGNUP_FORM_ROUTES: Record<
  string,
  { path: string; step: number; backPath?: string }
> = {
  apple: { path: '/signup/apple/', step: 0 },
  google: { path: '/signup/google/', step: 0 },
};
export const SIGNUP_FORM_PATHS = Object.values(SIGNUP_FORM_ROUTES).map(
  (route) => route.path
);
export const MAX_INPUT_LENGTH = 30;
export const MAX_EMAIL_INPUT_LENGTH = 254;

/**
 * Minimum username length for NEW accounts during signup.
 *
 * IMPORTANT: Some grandfathered accounts have 2-character usernames that were created
 * before this constraint was enforced. When validating existing usernames (e.g., in
 * shop routes, search, or authentication flows), do NOT use this constant as it will
 * reject legitimate 2-character usernames. Only use this for NEW user signup validation.
 */
export const MIN_USERNAME_LENGTH = 3;
export const USERNAME_VALIDATION = /^[A-Za-z0-9_]+$/;

/** Minimum age needed to hold a Depop account. */
export const MINIMUM_AGE = 13;

export const MIN_DAY = 1;
export const MAX_DAY = 31;
export const MIN_MONTH = 1;
export const MAX_MONTH = 12;
export const MIN_YEAR = 1900;
export const MAX_YEAR = new Date().getFullYear();
// Matches characters not in the specified set
export const DISALLOWED_USERNAME_CHARACTERS = /[^A-Za-z0-9_]/g;

export const SIGNUP_ERROR_TEXT: { [key: string]: string } = {
  SIGNUP_EMAIL_TAKEN: 'Signup.EmailTaken',
  USERNAME_INVALID: 'Username.Invalid',
  USERNAME_TAKEN: 'Username.Unavailable',
  DEVICE_BANNED: 'Signup.DeviceBanned',
  EMAIL_TAKEN: 'Signup.EmailTaken',
  USERNAME_ILLEGAL: 'Username.Unavailable',
  VERIFICATION_CODE_EXPIRED: 'Signup.VerificationCodeExpired',
  AGE_TOO_YOUNG: 'Signup.AgeTooYoung',
};

export const UNDER_AGE_ERROR_CODE = '1131';

export const USERNAME_VALIDITY_ERROR_CODE_MAP: Record<number, string> = {
  3100: 'Username.NotAllowed',
  3101: 'Username.NotAllowed',
  3105: 'Username.SimilarToEmail',
};

export const SIGNUP_VALIDATION_RULES: Partial<
  Record<
    keyof UserSignupDetails,
    Omit<
      // eslint-disable-next-line
      RegisterOptions<UserSignupDetails, any>,
      'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >
  >
> = {
  firstName: {
    required: {
      value: true,
      message: 'FirstName.Required',
    },
    maxLength: {
      value: MAX_INPUT_LENGTH,
      message: 'MaximumLength',
    },
  },
  lastName: {
    required: {
      value: true,
      message: 'LastName.Required',
    },
    maxLength: {
      value: MAX_INPUT_LENGTH,
      message: 'MaximumLength',
    },
  },
  username: {
    required: {
      value: true,
      message: 'Username.Required',
    },
    minLength: {
      value: MIN_USERNAME_LENGTH,
      message: 'Username.Length',
    },
    maxLength: {
      value: MAX_INPUT_LENGTH,
      message: 'MaximumLength',
    },
    pattern: {
      value: USERNAME_VALIDATION,
      message: 'Username.Invalid',
    },
  },
  email: {
    required: {
      value: true,
      message: 'Email.Required',
    },
    maxLength: {
      value: MAX_EMAIL_INPUT_LENGTH,
      message: 'MaximumLength',
    },
  },
  dobDay: {
    required: { value: true, message: 'DateOfBirth.SubmitRequired' },
    validate: {
      range: validateDobRange(MIN_DAY, MAX_DAY, 'DateOfBirth.DayRequired'),
      validDate: validateDay,
    },
  },
  dobMonth: {
    required: { value: true, message: 'DateOfBirth.SubmitRequired' },
    validate: validateDobRange(
      MIN_MONTH,
      MAX_MONTH,
      'DateOfBirth.MonthRequired'
    ),
  },
  dobYear: {
    required: { value: true, message: 'DateOfBirth.SubmitRequired' },
    validate: validateDobRange(MIN_YEAR, MAX_YEAR, 'DateOfBirth.YearRequired'),
  },
};

export const PHONE_CODES_LIST = Object.values(PHONE_CODES);
export const NEW_INPUT_STYLE_OPTIONS = PHONE_CODES_LIST.map((region) => ({
  label: region.name,
  value: region.code,
  activeLabel: region.prefix,
}));
